import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Video, Mic, MicOff, VideoOff } from "lucide-react";
import { API } from "../lib/api";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const RTC_CONFIG = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] };

export function CallProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pendingIce = useRef([]);
  const [status, setStatus] = useState("idle"); // idle|outgoing|incoming|active
  const [peer, setPeerState] = useState(null); // {id, name}
  const peerRef = useRef(null);
  const directionRef = useRef("outgoing");
  const connectedRef = useRef(false);
  const loggedRef = useRef(false);
  const incomingOffer = useRef(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);

  const setPeer = (p) => { peerRef.current = p; setPeerState(p); };

  const doLog = (statusVal) => {
    const p = peerRef.current;
    if (!p || loggedRef.current) return;
    loggedRef.current = true;
    api.post("/calls/log", { peer_id: p.id, peer_name: p.name, direction: directionRef.current, status: statusVal })
      .then(() => window.dispatchEvent(new Event("fv-refresh-calls")))
      .catch(() => {});
  };

  const send = (obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) { try { ws.send(JSON.stringify(obj)); return true; } catch {} }
    return false;
  };

  const waitForWs = () => new Promise((resolve) => {
    const t0 = Date.now();
    const check = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return resolve(true);
      if (Date.now() - t0 > 4000) return resolve(false);
      setTimeout(check, 120);
    };
    check();
  });

  const cleanup = useCallback(() => {
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pendingIce.current = [];
    incomingOffer.current = null;
    setStatus("idle");
    setPeer(null);
    setMuted(false);
    setCamOff(false);
    connectedRef.current = false;
    loggedRef.current = false;
  }, []);

  const newPc = useCallback((peerId) => {
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "ice", to: peerId, candidate: e.candidate }); };
    pc.ontrack = (e) => { if (remoteVideoRef.current) remoteVideoRef.current.srcObject = e.streams[0]; };
    pcRef.current = pc;
    return pc;
  }, []);

  const getMedia = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    localStreamRef.current = stream;
    setTimeout(() => { if (localVideoRef.current) localVideoRef.current.srcObject = stream; }, 100);
    return stream;
  };

  const startCall = useCallback(async (peerId, peerName) => {
    try {
      directionRef.current = "outgoing";
      connectedRef.current = false;
      loggedRef.current = false;
      setPeer({ id: peerId, name: peerName });
      setStatus("outgoing");
      const ready = await waitForWs();
      if (!ready) { cleanup(); alert("Connection not ready — please try again in a moment."); return; }
      const pc = newPc(peerId);
      const stream = await getMedia();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sent = send({ type: "call-invite", to: peerId, offer });
      if (!sent) { cleanup(); alert("Could not reach the server — please try again."); }
    } catch (e) { cleanup(); alert("Camera/mic unavailable: " + e.message); }
  }, [newPc, cleanup]);

  const accept = useCallback(async () => {
    try {
      directionRef.current = "incoming";
      loggedRef.current = false;
      const pc = newPc(peer.id);
      const stream = await getMedia();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(incomingOffer.current));
      for (const c of pendingIce.current) { try { await pc.addIceCandidate(c); } catch {} }
      pendingIce.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "call-answer", to: peer.id, answer });
      connectedRef.current = true;
      setStatus("active");
    } catch (e) { cleanup(); }
  }, [peer, newPc, cleanup]);

  const decline = useCallback(() => { if (peer) send({ type: "call-reject", to: peer.id }); doLog("declined"); cleanup(); }, [peer, cleanup]);
  const hangup = useCallback(() => { if (peer) send({ type: "call-end", to: peer.id }); doLog(connectedRef.current ? "answered" : "canceled"); cleanup(); }, [peer, cleanup]);

  const toggleMute = () => { const t = localStreamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMuted(!t.enabled); } };
  const toggleCam = () => { const t = localStreamRef.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCamOff(!t.enabled); } };

  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem("fv_token");
    if (!token) return;
    const wsUrl = API.replace(/^http/, "ws") + `/ws?token=${token}`;
    let closedByUs = false;
    let retry;

    const handleMessage = async (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      const from = data.from;
      if (data.type === "chat") { window.dispatchEvent(new CustomEvent("fv-chat", { detail: data.message })); window.dispatchEvent(new Event("fv-refresh-badge")); return; }
      if (data.type === "call-invite") {
        if (pcRef.current || incomingOffer.current) { send({ type: "call-reject", to: from }); return; }
        incomingOffer.current = data.offer;
        setPeer({ id: from, name: data.from_name });
        setStatus("incoming");
      } else if (data.type === "call-answer") {
        try { await pcRef.current?.setRemoteDescription(new RTCSessionDescription(data.answer)); connectedRef.current = true; setStatus("active"); } catch {}
      } else if (data.type === "ice") {
        const c = new RTCIceCandidate(data.candidate);
        if (pcRef.current?.remoteDescription) { try { await pcRef.current.addIceCandidate(c); } catch {} }
        else pendingIce.current.push(c);
      } else if (data.type === "call-reject") {
        doLog(connectedRef.current ? "answered" : "declined");
        cleanup();
      } else if (data.type === "call-end") {
        doLog(connectedRef.current ? "answered" : "missed");
        cleanup();
      } else if (data.type === "call-unavailable") {
        doLog("missed");
        cleanup();
        setTimeout(() => alert("They're not online right now — we've sent them a missed-call notice."), 50);
      }
    };

    const connect = () => {
      let ws;
      try { ws = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = ws;
      ws.onmessage = handleMessage;
      ws.onclose = () => {
        wsRef.current = null;
        if (!closedByUs) { retry = setTimeout(connect, 2000); }
      };
    };
    connect();

    return () => { closedByUs = true; clearTimeout(retry); try { wsRef.current?.close(); } catch {} };
  }, [user?.id, cleanup]);

  return (
    <CallContext.Provider value={{ startCall, status }}>
      {children}
      <AnimatePresence>
        {status !== "idle" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] mx-auto flex flex-col" style={{ maxWidth: 480, background: "#0A0A0F" }} data-testid="call-modal">
            <div className="relative flex-1">
              <video ref={remoteVideoRef} autoPlay playsInline className="absolute inset-0 h-full w-full object-cover" />
              {status !== "active" && (
                <div className="absolute inset-0 grid place-items-center text-center px-8">
                  <div>
                    <div className="h-24 w-24 rounded-full mx-auto grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
                      <span className="font-serif-display text-4xl gold-text">{(peer?.name || "?").charAt(0)}</span>
                    </div>
                    <div className="font-serif-display text-3xl mt-4 text-white">{peer?.name}</div>
                    <div className="text-sm mt-1 text-white/60">{status === "incoming" ? "Incoming video call…" : "Calling…"}</div>
                  </div>
                </div>
              )}
              <video ref={localVideoRef} autoPlay playsInline muted className="absolute bottom-4 right-4 h-40 w-28 object-cover rounded-xl border border-white/20" />
            </div>
            <div className="p-6 flex items-center justify-center gap-5">
              {status === "active" && (
                <>
                  <button onClick={toggleMute} data-testid="call-mute" className="h-12 w-12 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.15)" }}>
                    {muted ? <MicOff size={20} color="#fff" /> : <Mic size={20} color="#fff" />}
                  </button>
                  <button onClick={toggleCam} className="h-12 w-12 rounded-full grid place-items-center" style={{ background: "rgba(255,255,255,0.15)" }}>
                    {camOff ? <VideoOff size={20} color="#fff" /> : <Video size={20} color="#fff" />}
                  </button>
                </>
              )}
              {status === "incoming" && (
                <button onClick={accept} data-testid="call-accept" className="h-16 w-16 rounded-full grid place-items-center" style={{ background: "#2ecc71" }}>
                  <Phone size={26} color="#fff" />
                </button>
              )}
              <button onClick={status === "incoming" ? decline : hangup} data-testid="call-hangup" className="h-16 w-16 rounded-full grid place-items-center" style={{ background: "var(--destructive)" }}>
                <PhoneOff size={26} color="#fff" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </CallContext.Provider>
  );
}
